import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaRocket,
  FaChartLine,
  FaShieldAlt,
  FaUsers,
  FaBookOpen,
  FaArrowRight,
  FaGem,
  FaBuilding,
  FaUserPlus,
  FaClipboardList,
  FaUserCheck,
  FaChartPie,
  FaFileAlt,
} from "react-icons/fa";
import { MdOutlineSupportAgent } from "react-icons/md";
import { HiOutlineLightBulb } from "react-icons/hi";
import Button from "components/button/Button";
import { HiOutlineBuildingOffice, HiOutlinePlusCircle } from "react-icons/hi2";
import { TbLicense } from "react-icons/tb";

const CompanyView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // const { user } = useAppSelector((state) => state.auth);
  const [showModal, setShowModal] = useState(false);

  // const hasNoCompany = user?.companyCount === 0 || !user?.companyCount;

  // useEffect(() => {
  //   if (hasNoCompany) {
  //     setShowModal(true);
  //   }
  // }, [hasNoCompany]);

  const handleCreateCompany = () => {
    setShowModal(false);
    navigate("/company/companies/create");
  };

  const features = [
    {
      icon: <FaRocket className="h-5 w-5" />,
      title: t("home.feature1"),
      description: t("home.feature1Desc"),
      color: "bg-blue-500",
    },
    {
      icon: <FaChartLine className="h-5 w-5" />,
      title: t("home.feature2"),
      description: t("home.feature2Desc"),
      color: "bg-green-500",
    },
    {
      icon: <FaShieldAlt className="h-5 w-5" />,
      title: t("home.feature3"),
      description: t("home.feature3Desc"),
      color: "bg-purple-500",
    },
    {
      icon: <FaUsers className="h-5 w-5" />,
      title: t("home.feature4"),
      description: t("home.feature4Desc"),
      color: "bg-orange-500",
    },
  ];

  const guideSteps = [
    {
      step: 1,
      icon: <FaBuilding className="h-5 w-5" />,
      title: t("home.step1"),
      description: t("home.step1Desc"),
      color: "from-blue-500 to-blue-600",
    },
    {
      step: 2,
      icon: <FaUserPlus className="h-5 w-5" />,
      title: t("home.step2"),
      description: t("home.step2Desc"),
      color: "from-green-500 to-green-600",
    },
    {
      step: 3,
      icon: <TbLicense className="h-5 w-5" />,
      title: t("home.step3"),
      description: t("home.step3Desc"),
      color: "from-purple-500 to-purple-600",
    },
    {
      step: 4,
      icon: <FaClipboardList className="h-5 w-5" />,
      title: t("home.step4"),
      description: t("home.step4Desc"),
      color: "from-orange-500 to-orange-600",
    },
    {
      step: 5,
      icon: <FaUserCheck className="h-5 w-5" />,
      title: t("home.step5"),
      description: t("home.step5Desc"),
      color: "from-pink-500 to-pink-600",
    },
    {
      step: 6,
      icon: <FaChartPie className="h-5 w-5" />,
      title: t("home.step6"),
      description: t("home.step6Desc"),
      color: "from-indigo-500 to-indigo-600",
    },
    {
      step: 7,
      icon: <FaFileAlt className="h-5 w-5" />,
      title: t("home.step7"),
      description: t("home.step7Desc"),
      color: "from-red-500 to-red-600",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-navy-900 dark:via-navy-800 dark:to-navy-900">
      <nav className="sticky top-0 bg-white/80 backdrop-blur-lg dark:bg-navy-800/80 dark:backdrop-blur-lg">
        <div className="container mx-auto flex flex-wrap items-center justify-between gap-4 px-4 py-3">
          <div className="flex items-center gap-2">
            <FaGem className="h-7 w-7 text-brand-500" />
            <span className="text-lg font-bold text-gray-800 dark:text-white">
              {t("home.appName")}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/about"
              className="rounded-lg bg-brand-500 px-4 py-1.5 text-sm font-medium text-white transition-all hover:bg-brand-600 hover:shadow-lg"
            >
              {t("home.about")}
            </Link>
            <Link
              to="/contact"
              className="rounded-lg bg-brand-500 px-4 py-1.5 text-sm font-medium text-white transition-all hover:bg-brand-600 hover:shadow-lg"
            >
              {t("home.contact")}
            </Link>
          </div>
        </div>
      </nav>

      <section className="relative overflow-hidden py-16 md:py-20">
        <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-brand-200 opacity-20 blur-3xl dark:bg-brand-700" />
        <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-purple-200 opacity-20 blur-3xl dark:bg-purple-700" />
        <div className="absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-200 opacity-10 blur-3xl dark:bg-blue-700" />

        <div className="container relative mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-4xl text-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
              className="mb-4 inline-flex items-center gap-2 rounded-full bg-brand-50 px-4 py-1.5 dark:bg-brand-500/30"
            >
              <HiOutlineLightBulb className="h-4 w-4 text-brand-600 dark:text-brand-200" />
              <span className="text-xs font-medium text-brand-800 dark:text-brand-200">
                {t("home.badge")}
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-transparent mb-4 text-3xl font-extrabold dark:text-gray-300 md:text-4xl lg:text-5xl"
            >
              {t("home.welcome")}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mx-auto mb-8 max-w-2xl text-base text-gray-600 dark:text-gray-300 md:text-lg"
            >
              {t("home.subtitle")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-wrap items-center justify-center gap-3"
            >
              <Button
                variant="primary"
                className="group inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-brand-500 to-brand-600 px-6 py-3 text-sm font-medium text-white transition-all hover:shadow-lg hover:shadow-brand-200 dark:hover:shadow-brand-900/30"
                onClick={() => navigate("/company/companies/create")}
                startIcon={
                  <FaArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                }
              >
                {t("home.start")}
              </Button>
              <button
                onClick={() => {
                  document.getElementById("guide-section")?.scrollIntoView({
                    behavior: "smooth",
                  });
                }}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-6 py-3 text-sm font-medium text-gray-700 transition-all hover:border-brand-300 hover:bg-brand-50 hover:text-brand-600 dark:border-gray-700 dark:text-gray-300 dark:hover:border-brand-400 dark:hover:bg-brand-900/20"
              >
                <FaBookOpen className="h-4 w-4" />
                {t("home.guide")}
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mb-8 text-center"
        >
          <h2 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
            {t("home.whyUs")}
          </h2>
          <p className="mx-auto max-w-2xl text-sm text-gray-600 dark:text-gray-400">
            {t("home.whyUsDesc")}
          </p>
        </motion.div>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group relative overflow-hidden rounded-2xl bg-brand-100/10 p-6 shadow-xl transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl dark:bg-navy-800"
            >
              {/* Background Gradient Effect */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 transition-opacity duration-300 group-hover:opacity-5`}
              />

              {/* Glow Effect */}
              <div
                className={`absolute -right-10 -top-10 h-32 w-32 rounded-full ${feature.color} opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-20`}
              />

              <div className="relative">
                {/* Icon with Ring */}
                <div className="relative mb-4 inline-flex">
                  <div
                    className={`absolute inset-0 rounded-xl ${feature.color} opacity-20 blur-md transition-opacity duration-300 group-hover:opacity-40`}
                  />
                  <div
                    className={`relative inline-flex rounded-xl ${feature.color} p-3 text-white shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl`}
                  >
                    {feature.icon}
                  </div>
                </div>

                {/* Title with underline effect */}
                <h3 className="mb-2 text-base font-bold text-gray-900 transition-colors duration-300 group-hover:text-brand-600 dark:text-white dark:group-hover:text-brand-200">
                  {feature.title}
                  <span
                    className={`mt-2 block h-0.5 w-0 rounded-full ${feature.color} transition-all duration-300 group-hover:w-full`}
                  />
                </h3>

                {/* Description */}
                <p className="text-sm leading-relaxed text-gray-700 transition-colors duration-300 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-300">
                  {feature.description}
                </p>
              </div>

              {/* Border Animation */}
              <div
                className={`absolute bottom-0 left-0 h-1 w-0 rounded-b-2xl ${feature.color} transition-all duration-500 group-hover:w-full`}
              />
            </motion.div>
          ))}
        </div>
      </section>

      <section id="guide-section" className="container mx-auto px-4 py-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mb-8 text-center"
        >
          <h2 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
            {t("home.guideTitle")}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t("home.guideDesc")}
          </p>
        </motion.div>

        <div className="relative">
          <div className="absolute left-1/2 top-0 h-full w-1 -translate-x-1/2 bg-gradient-to-b from-brand-300 via-brand-400 to-purple-300 dark:from-brand-700 dark:via-brand-600 dark:to-purple-700" />

          <div className="space-y-6">
            {guideSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative flex items-center gap-4 md:gap-10 ${
                  index % 2 === 0 ? "flex-row" : "flex-row-reverse"
                }`}
              >
                <div
                  className={`w-5/12 ${
                    index % 2 === 0
                      ? "text-left ltr:text-right"
                      : "text-right ltr:text-left"
                  }`}
                >
                  <div
                    className={`inline-block rounded-xl bg-gradient-to-r ${step.color} p-4 text-white shadow-lg`}
                  >
                    {step.icon}
                  </div>
                </div>

                <div className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-500 text-white shadow-lg ring-4 ring-white dark:ring-navy-800">
                  <span className="text-sm font-bold">{step.step}</span>
                </div>

                <div
                  className={`w-5/12 ${
                    index % 2 === 0
                      ? "text-right ltr:text-left"
                      : "text-left ltr:text-right"
                  }`}
                >
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                    {step.title}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-brand-500 to-purple-600 p-8 text-center text-white dark:from-brand-700 dark:to-purple-800"
        >
          <div className="absolute left-0 top-0 h-full w-full opacity-10">
            <div className="absolute -left-16 -top-16 h-48 w-48 rounded-full bg-white" />
            <div className="absolute -bottom-16 -right-16 h-48 w-48 rounded-full bg-white" />
          </div>
          <div className="relative z-10">
            <h2 className="mb-3 text-2xl font-bold">{t("home.ctaTitle")}</h2>
            <p className="mx-auto mb-6 max-w-2xl text-sm text-white/90">
              {t("home.ctaDesc")}
            </p>
            <Button
              variant="secondary"
              className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-2.5 text-sm text-brand-600 transition-all hover:scale-105 hover:shadow-xl"
              onClick={() => navigate("/company/companies/create")}
              startIcon={<FaArrowRight className="h-3.5 w-3.5" />}
            >
              {t("home.ctaButton")}
            </Button>

            <button
              onClick={() => navigate("/contact")}
              className="ms-4 inline-flex items-center gap-2 rounded-lg border border-white/40 bg-white/10 px-6 py-2.5 text-sm font-medium text-white backdrop-blur-sm transition-all hover:scale-105 hover:bg-white/20 hover:shadow-xl"
            >
              <MdOutlineSupportAgent className="h-4 w-4" />
              {t("home.ctaButtonSecondary")}
            </button>
          </div>
        </motion.div>
      </section>

      <footer className="border-t border-gray-200 dark:border-navy-700">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col items-center justify-between gap-3 md:flex-row">
            <div className="flex items-center gap-2">
              <FaGem className="h-5 w-5 text-brand-500" />
              <span className="font-bold text-gray-800 dark:text-white">
                {t("home.app")}
              </span>
            </div>
            <div className="flex gap-4">
              <Link
                to="/about"
                className="text-xs text-gray-600 transition-colors hover:text-brand-500 dark:text-gray-400 dark:hover:text-brand-400"
              >
                {t("home.about")}
              </Link>
              {/* <Link
                to="/privacy"
                className="text-xs text-gray-600 transition-colors hover:text-brand-500 dark:text-gray-400 dark:hover:text-brand-400"
              >
                {t("home.privacy")}
              </Link>
              <Link
                to="/terms"
                className="text-xs text-gray-600 transition-colors hover:text-brand-500 dark:text-gray-400 dark:hover:text-brand-400"
              >
                {t("home.terms")}
              </Link> */}
              <Link
                to="/contact"
                className="flex items-center gap-1 text-xs text-gray-600 transition-colors hover:text-brand-500 dark:text-gray-400 dark:hover:text-brand-400"
              >
                <MdOutlineSupportAgent className="h-3.5 w-3.5" />
                {t("home.contact")}
              </Link>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-500">
              © {new Date().getFullYear()} {t("home.app")}.{" "}
              {t("home.copyright")}
            </p>
          </div>
        </div>
      </footer>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center px-4 backdrop-blur-md"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white p-6 shadow-2xl dark:bg-navy-800"
            >
              <button
                onClick={() => setShowModal(false)}
                className="absolute right-4 top-4 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-navy-700 dark:hover:text-gray-300"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, type: "spring", bounce: 0.5 }}
                className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/30"
              >
                <HiOutlineBuildingOffice className="h-8 w-8 text-brand-600 dark:text-brand-400" />
              </motion.div>

              <h3 className="text-center text-lg font-bold text-navy-700 dark:text-white">
                {t("company.noCompanyTitle")}
              </h3>

              <div className="mt-2 text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t("company.noCompanyDesc")}
                </p>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-4 space-y-2 rounded-xl bg-gray-50 p-4 dark:bg-navy-700"
              >
                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <span className="text-brand-500">✓</span>
                  {t("company.feature1")}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <span className="text-brand-500">✓</span>
                  {t("company.feature2")}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <span className="text-brand-500">✓</span>
                  {t("company.feature3")}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <span className="text-brand-500">✓</span>
                  {t("company.feature4")}
                </div>
              </motion.div>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <Button
                  variant="primary"
                  className="flex w-full items-center justify-center rounded-xl p-3"
                  onClick={handleCreateCompany}
                  startIcon={<HiOutlinePlusCircle className="h-5 w-5" />}
                >
                  {t("company.createCompany")}
                </Button>

                <Button
                  variant="outline"
                  className="w-full rounded-xl px-6 py-3"
                  onClick={() => setShowModal(false)}
                >
                  {t("buttons.cancel")}
                </Button>
              </div>

              <p className="mt-3 text-center text-xs text-gray-400 dark:text-gray-500">
                {t("company.noCompanyHelp")}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CompanyView;
